<div class="h-full w-full flex flex-col justify-start items-center overflow-hidden">
    <div class="w-full h-full grid grid-cols-4 gap-2">

        <!-- Left Hand Sidebar - Stepper -->
        <div class="col-span-1 h-full rounded-xl border border-gray-200 dark:border-white/10">
            <div class="h-full flex flex-col overflow-hidden divide-y divide-border">
                <!-- header -->
                <div class="px-2 py-4 drawer-header-primary overflow-hidden rounded-t-xl">
                    <div class="flex space-x-3 h-full items-center">
                        <div class="min-w-0 flex-1 px-2">
                            <p class="text-md font-semibold text-primary">
                                Build your screening brief
                            </p>
                            <p class="text-sm text-secondary">
                                Complete all steps to launch your brief.
                            </p>
                        </div>
                    </div>
                </div>
                <!-- header - END -->
                <!-- main content -->
                <nav aria-label="Screening brief steps"
                    class="relative h-full flex-1 flex-col items-center justify-center w-full overflow-hidden p-2">
                    @for (step of steps(); track step.id; let i = $index) {
                    <button type="button" (click)="goTo(i)" [attr.aria-current]="i === current() ? 'step' : null"
                        class="mb-0.5 flex w-full items-center gap-3 px-2 py-2.5 text-left transition-colors hover:bg-muted"
                        [class]="i === current() ? 'bg-brand-studio/10 rounded-md' : ''">

                        <span
                            class="grid size-[30px] flex-shrink-0 place-items-center rounded-full border-[1.5px] font-heading text-sm font-bold transition-all"
                            [class]="stepCircleClass(i)">
                            @if (isDone(i)) {
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                                class="size-5" stroke-linejoin="round" aria-hidden="true">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            } @else {
                            {{ i + 1 }}
                            }
                        </span>
                        <span>
                            <span class="block font-heading text-base font-bold leading-tight"
                                [class]="i === current() ? 'text-foreground' : 'text-foreground/80'">{{ step.label
                                }}</span>
                            <span class="block text-[11px] text-muted-foreground">{{ step.sub }}</span>
                        </span>
                    </button>
                    }
                </nav>
                <!-- main content - END -->
            </div>
        </div>
        <!-- Left Hand Sidebar - Stepper - END -->


        <!-- Main Content -->
        <div class="col-span-3 h-full overflow-hidden flex flex-col gap-y-2">
            <div class="w-full flex-1 overflow-hidden">
                <div #scrollArea class="h-full overflow-y-auto">
                    <div class="mx-auto max-w-3xl px-6 sm:px-12">

                        <div class="mb-7">
                            <span class="text-[11px] font-bold uppercase tracking-wider text-brand-studio-dark dark:text-brand-studio">{{
                                eyebrow() }}</span>
                            <h2 class="mt-2.5 font-heading text-4xl font-bold leading-none tracking-tight">{{
                                step().title }}</h2>
                            <p class="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">{{ step().desc }}</p>
                        </div>

                        @switch (step().id) {
                            @case ('product') {
                                <sbs-product-select />
                            }

                            @case ('define') {
                                <sbs-screening-define />
                            }

                            @case ('redflags') {
                                <sbs-screening-red-flags />
                            }

                            @case ('report') {
                                <sbs-screening-report-type />
                            }

                            @case ('review') {
                                <sbs-screening-review />
                            }

                            @case ('creators') {
                                <sbs-creator-select-method />
                            }
                        }

                    </div>
                </div>
            </div>


            <div
                class="shrink-0 w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 flex flex-row items-center justify-between">
                <div class="flex-1 flex flex-row items-center justify-start gap-x-4">
                    <button type="button" class="secondary-button inline-flex items-center gap-2 disabled:opacity-40"
                        [disabled]="current() === 0" (click)="back()">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            aria-hidden="true">
                            <path d="m12 19-7-7 7-7" />
                            <path d="M19 12H5" />
                        </svg>
                        Back
                    </button>

                    <div class="max-w-sm flex-1">
                        <div class="h-1.5 overflow-hidden rounded-full bg-muted">
                            <span class="block h-full rounded-full bg-brand-studio transition-all"
                                [style.width.%]="progressPct()"></span>
                        </div>
                        <div class="mt-1.5 text-[11px] text-muted-foreground">Step {{ current() + 1 }} of {{
                            steps().length }} ·
                            {{ essentialsDone() }}/{{ checklist().length }} essentials done</div>
                    </div>
                </div>

                <div class="shrink-0 flex flex-row items-center justify-end gap-x-4">
                    <button type="button" class="flashy-go-button" (click)="primary()"
                        [attr.data-guide-id]="step().id === 'creators' ? 'screening.launch' : 'screening.continue'">
                        @switch (step().id) {
                        @case ('review') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="size-4" aria-hidden="true">
                            <path d="m22 22-5.5-5.5" />
                            <path d="M3 11a8 8 0 0 0 8 8 8 8 0 0 0 8-8 8 8 0 0 0-8-8 8 8 0 0 0-8 8Z" />
                            <path d="M8 11h6" />
                        </svg>
                        {{ primaryLabel() }}
                        }
                        @case ('creators') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="size-4" aria-hidden="true">
                            <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                            <path d="m21.854 2.147-10.94 10.939" />
                        </svg>
                        {{ primaryLabel() }}
                        }
                        @default {
                        {{ primaryLabel() }}
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            aria-hidden="true">
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                        }
                        }
                    </button>
                </div>
            </div>

        </div>
        <!-- Main Content - END -->

    </div>
</div>

@if (checkOpen()) {
<sbs-screening-overlay [stages]="preflightStages" headIcon="scan" headline="Checking Your Brief" subLead="Sending"
    [subject]="briefName()" subTrail="to the server for a quick review…" [result]="checkResult()"
    [resultTitle]="checkResultTitle()" [resultSub]="checkResultSub()" [reasons]="checkReasons()"
    (sequenceComplete)="onCheckSequenceComplete()">

    <div overlayActions class="contents">
        @if (checkResult() === 'pass') {
        <button type="button" class="flashy-go-button" (click)="closeCheckAndGoTo('creators')">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4"
                aria-hidden="true">
                <path d="M18 21a8 8 0 0 0-16 0" />
                <circle cx="10" cy="8" r="5" />
                <path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
            </svg>
            Add creators
        </button>
        <button type="button" (click)="closeCheck()"
            class="rounded-full border border-white/40 px-5 py-2.5 text-sm font-bold text-white/90 transition-colors hover:border-white hover:bg-white/10 hover:text-white">
            Review brief
        </button>
        } @else {
        <button type="button" class="flashy-go-button" (click)="closeCheckAndGoTo('define')">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4"
                aria-hidden="true">
                <path d="M13 21h8" />
                <path d="m15 5 4 4" />
                <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
            </svg>
            Update the brief
        </button>
        <button type="button" (click)="closeCheckAndGoTo('redflags')"
            class="rounded-full border border-white/40 px-5 py-2.5 text-sm font-bold text-white/90 transition-colors hover:border-white hover:bg-white/10 hover:text-white">
            Add red flags
        </button>
        @if (essentialsComplete()) {
        <button type="button" (click)="continueAnyway()"
            class="inline-flex items-center gap-2 rounded-full border border-white/40 px-5 py-2.5 text-sm font-bold text-white/90 transition-colors hover:border-white hover:bg-white/10 hover:text-white">
            Continue anyway
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
            </svg>
        </button>
        }
        }
    </div>
</sbs-screening-overlay>
}
